import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Close";
import type {
  Callout,
  CellReferenceInput,
  CalloutSource,
} from "../../models/API";

interface CalloutEditorProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with a payload shaped like a `Callout`. The viewer is responsible
   * for choosing addCallout vs updateCallout based on `mode`.
   */
  onSave: (callout: Callout) => void;
  mode: "create" | "edit";
  initialData?: Callout;
  context: {
    teamId: number;
    week: string;
    reportName: string;
    pageNum: number;
    userId: number;
  };
  error?: string | null;
}

const STATUS_OPTIONS = [
  { value: "positive", label: "Positive" },
  { value: "watch", label: "Watch" },
];

const TREND_OPTIONS = [
  { value: "", label: "None" },
  { value: "Ongoing Trend", label: "Ongoing Trend" },
  { value: "New Signal", label: "New Signal" },
  { value: "Reversal", label: "Reversal" },
  { value: "Trend Deceleration", label: "Trend Deceleration" },
  { value: "Trend Acceleration", label: "Trend Acceleration" },
];

// Cell-ref validation — mirrors the inlined resolver logic in
// addCallout.js / updateCallout.js. See requirements Req 13 + design Decision #11.
const COL_RE = /^[A-Z]+$/;
const ROW_SINGLE = /^\d+$/;
const ROW_RANGE = /^\d+-\d+$/;
const ROW_FULLREF = /^[A-Z]+\d+-[A-Z]*\d+$/;
const MAX_CELL_REFS = 50;
const MAX_CELL_FIELD_LEN = 100;

interface EditableRef {
  row: string;
  col: string;
}

/**
 * Returns null if valid, an error message string otherwise. Mirrors the
 * resolver-side validator. Three accepted patterns:
 *  - col=letters, row=digits (e.g. col='G', row='3' → cell G3)
 *  - col=letters, row=digit-range (e.g. col='D', row='24-36' → D24:D36)
 *  - col='', row=full Excel ref (e.g. row='D24-D36')
 */
function validateCellRef(ref: EditableRef): string | null {
  const row = ref.row.trim().toUpperCase();
  const col = ref.col.trim().toUpperCase();
  if (!row && !col) {
    return "Row or col required";
  }
  if (row.length > MAX_CELL_FIELD_LEN || col.length > MAX_CELL_FIELD_LEN) {
    return `Field exceeds ${MAX_CELL_FIELD_LEN} chars`;
  }
  if (COL_RE.test(col) && (ROW_SINGLE.test(row) || ROW_RANGE.test(row))) {
    return null;
  }
  if (col === "" && ROW_FULLREF.test(row)) {
    return null;
  }
  return "Invalid (e.g. col='G' row='3', col='D' row='24-36', or col='' row='D24-D36')";
}

const CalloutEditor: React.FC<CalloutEditorProps> = ({
  open,
  onClose,
  onSave,
  mode,
  initialData,
  context,
  error,
}) => {
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState<string>("positive");
  const [trend, setTrend] = useState<string>("");
  const [impact, setImpact] = useState<string>("");
  const [whyText, setWhyText] = useState("");
  const [cellRefs, setCellRefs] = useState<EditableRef[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form when dialog opens or initialData changes
  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initialData) {
      setTitle(initialData.title);
      setStatus(initialData.status);
      setTrend(initialData.trend ?? "");
      setImpact(initialData.impact ?? "");
      setWhyText(initialData.why_text ?? "");
      setCellRefs(
        (initialData.cell_refs ?? []).map((r) => ({ row: r.row, col: r.col }))
      );
    } else {
      setTitle("");
      setStatus("positive");
      setTrend("");
      setImpact("");
      setWhyText("");
      setCellRefs([]);
    }
    setValidationError(null);
  }, [open, mode, initialData]);

  // Per-row validation results (memo via useMemo would be overkill — list is small)
  const refErrors = cellRefs.map(validateCellRef);
  const anyRefInvalid = refErrors.some((e) => e !== null);
  const tooMany = cellRefs.length > MAX_CELL_REFS;

  const handleAddRef = () => {
    if (cellRefs.length >= MAX_CELL_REFS) return;
    setCellRefs([...cellRefs, { row: "", col: "" }]);
  };

  const handleRemoveRef = (idx: number) => {
    setCellRefs(cellRefs.filter((_, i) => i !== idx));
  };

  const handleRefChange = (
    idx: number,
    field: "row" | "col",
    value: string
  ) => {
    setCellRefs(
      cellRefs.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
    );
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!title.trim()) {
      setValidationError("Callout headline is required.");
      return;
    }
    if (!status) {
      setValidationError("Status is required.");
      return;
    }
    if (anyRefInvalid) {
      setValidationError("Fix the invalid cell references before saving.");
      return;
    }
    if (tooMany) {
      setValidationError(`Cell references: max ${MAX_CELL_REFS} allowed.`);
      return;
    }
    setValidationError(null);

    // Normalise the cell refs to match the validator's canonical form.
    const normalisedCellRefs: CellReferenceInput[] = cellRefs.map((r) => ({
      row: r.row.trim().toUpperCase(),
      col: r.col.trim().toUpperCase(),
    }));

    // Preserve the existing source when editing; default to "manual" on create.
    const source: CalloutSource = initialData?.source ?? "manual";

    const callout: Callout = {
      __typename: "Callout",
      id: initialData?.id ?? 0,
      team_id: context.teamId,
      week: context.week,
      report_name: context.reportName,
      page_num: context.pageNum,
      source,
      title: title.trim(),
      status,
      trend: trend,
      why_text: whyText.trim() || null,
      impact: impact,
      card_number: initialData?.card_number ?? 0,
      cell_refs: normalisedCellRefs.map((r) => ({
        __typename: "CellReference",
        ...r,
      })),
      creator_id: initialData?.creator_id ?? context.userId,
      creator_name: initialData?.creator_name ?? null,
      updated_by: context.userId,
      updated_by_name: initialData?.updated_by_name ?? null,
      created_at: initialData?.created_at ?? new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ai_generation_batch_id: initialData?.ai_generation_batch_id ?? null,
    };

    onSave(callout);
  };

  const displayError = error || validationError;
  const saveDisabled = anyRefInvalid || tooMany;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "create" ? "Add Callout" : "Edit Callout"}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
      >
        {displayError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {displayError}
          </Alert>
        )}

        <TextField
          label="Callout Headline"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
          autoFocus
          margin="dense"
        />

        <FormControl fullWidth required margin="dense">
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>Trend</InputLabel>
          <Select
            value={trend}
            label="Trend"
            onChange={(e) => setTrend(e.target.value)}
          >
            {TREND_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>Impact</InputLabel>
          <Select
            value={impact}
            label="Impact"
            onChange={(e) => setImpact(e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="large">Large</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="small">Small</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Why (optional)"
          value={whyText}
          onChange={(e) => setWhyText(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          margin="dense"
        />

        {/* Cell References */}
        <Box sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant="subtitle2">
              Cell References (optional)
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddRef}
              disabled={cellRefs.length >= MAX_CELL_REFS}
            >
              Add
            </Button>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: "block", color: "#757575", mb: 1 }}
          >
            Highlights the named cells in the source PDF. Examples: col=G row=3,
            or col=D row=24-36, or leave col empty and put D24-D36 in row.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "#E65100",
              mb: 1,
              fontStyle: "italic",
            }}
          >
            Note: It takes up to 5 minutes to apply cell references to the
            highlighted PDF. Please refresh after saving.
          </Typography>
          {cellRefs.map((ref, idx) => {
            const refError = refErrors[idx];
            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <TextField
                  label="Row"
                  value={ref.row}
                  onChange={(e) => handleRefChange(idx, "row", e.target.value)}
                  size="small"
                  error={refError !== null}
                  helperText={refError ?? " "}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Col"
                  value={ref.col}
                  onChange={(e) => handleRefChange(idx, "col", e.target.value)}
                  size="small"
                  error={refError !== null}
                  helperText={" "}
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => handleRemoveRef(idx)}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
          {tooMany && (
            <Typography variant="caption" color="error">
              Too many cell references — max {MAX_CELL_REFS} allowed.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saveDisabled}
        >
          {mode === "create" ? "Add" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalloutEditor;
